import { Link } from "@tanstack/react-router";
import { Calendar, MapPin } from "lucide-react";
import { useUserTimezone } from "hooks/useUserTimezone";
import { FavoriteButton } from "./FavoriteButton";

interface EventData {
    id: number;
    host: string | null;
    title: string;
    type: string;
    url: string | null;
    description: string | null;
    start: Date | string;
    end: Date | string | null;
    category: string | null;
    priceRange: string | null;
    location: string | null;
    imageUrl: string | null;
    thumbnailUrl: string | null;
}

export const EventCard = ({ event, target }: { event: EventData; target?: string }) => {
    const timeZone = useUserTimezone();
    const startDate = new Date(event.start);
    const month = startDate.toLocaleDateString("en-US", { month: "short", timeZone });
    const day = startDate.toLocaleDateString("en-US", { day: "numeric", timeZone });
    const time = startDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone,
    });

    return (
        <Link
            to="/events/$id"
            params={{ id: String(event.id) }}
            target={target}
            className="flex overflow-hidden rounded-2xl border border-red-200 bg-white no-underline shadow-sm transition-shadow hover:shadow-md"
        >
            <div className="flex w-20 shrink-0 flex-col items-center justify-center bg-red-50 p-3">
                <span className="text-xs font-semibold uppercase text-red-500">{month}</span>
                <span className="text-2xl font-bold text-red-600">{day}</span>
            </div>
            <div className="flex-1 p-4">
                <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4 text-red-500" />
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-600">
                            {event.type}
                        </span>
                    </div>
                    <FavoriteButton target={{ type: "event", id: event.id }} size={16} />
                </div>
                <h3 className="line-clamp-2 font-semibold text-gray-900">{event.title}</h3>
                <div className="mt-2 flex flex-col gap-1 text-xs text-gray-500">
                    <span>{time}</span>
                    {event.host && <span>Hosted by {event.host}</span>}
                    {event.location && (
                        <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {event.location}
                        </span>
                    )}
                </div>
            </div>
            {event.imageUrl && (
                <div className="hidden w-32 shrink-0 overflow-hidden sm:block">
                    <img
                        src={event.imageUrl}
                        alt={event.title}
                        className="h-full w-full object-cover"
                    />
                </div>
            )}
        </Link>
    );
};
